import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import '../Alert.css';

const Alert = ({
  message,
  title,
  type = 'info',
  onClose,
  duration = 2500,
  dismissOnClick = true,
  actionHref,
  onAction,
  actionLabel = 'View',
  onSecondaryAction,
  secondaryActionLabel,
  showProgress = true,
  showClose = !dismissOnClick,
  className = '',
}) => {
  const [visible, setVisible] = useState(false);
  const onCloseRef = useRef(onClose);
  const fadeTimerRef = useRef(null);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const closeAlert = useCallback(() => {
    setVisible(false);
    window.clearTimeout(fadeTimerRef.current);
    fadeTimerRef.current = window.setTimeout(() => {
      onCloseRef.current?.();
    }, 500);
  }, []);

  useEffect(() => {
    setVisible(true);
    window.clearTimeout(fadeTimerRef.current);
    const lifetime = Number(duration);
    const timer = Number.isFinite(lifetime) && lifetime > 0
      ? window.setTimeout(closeAlert, lifetime)
      : null;

    return () => {
      if (timer) window.clearTimeout(timer);
      window.clearTimeout(fadeTimerRef.current);
    };
  }, [closeAlert, duration, message, title, type]);

  const handleAlertClick = () => {
    if (dismissOnClick) closeAlert();
  };

  const lifetime = Number(duration);
  const hasCountdown = showProgress && Number.isFinite(lifetime) && lifetime > 0;

  return (
    <div
      className={`alert ${type} ${visible ? 'fade-in' : 'fade-out'}${dismissOnClick ? ' is-click-dismissible' : ''}${className ? ` ${className}` : ''}`}
      onClick={handleAlertClick}
      role={type === 'error' ? 'alert' : 'status'}
    >
      <div className="alert-copy">
        {title && <strong className="alert-title">{title}</strong>}
        {message && <span className="alert-message">{message}</span>}
        {(actionHref || onAction || onSecondaryAction) && (
          <div className="alert-actions">
            {actionHref && !onAction && (
              <a className="alert-action" href={actionHref} onClick={(event) => event.stopPropagation()}>
                {actionLabel}
              </a>
            )}
            {onAction && (
              <button
                type="button"
                className="alert-action"
                onClick={(event) => {
                  event.stopPropagation();
                  onAction(event);
                }}
              >
                {actionLabel}
              </button>
            )}
            {onSecondaryAction && (
              <button
                type="button"
                className="alert-action"
                onClick={(event) => {
                  event.stopPropagation();
                  onSecondaryAction(event);
                }}
              >
                {secondaryActionLabel || 'Continue'}
              </button>
            )}
          </div>
        )}
      </div>
      {showClose && (
        <button
          type="button"
          className="alert-close"
          onClick={(event) => {
            event.stopPropagation();
            closeAlert();
          }}
          aria-label="Dismiss notification"
          title="Dismiss"
        >
          <X size={17} />
        </button>
      )}
      {hasCountdown && (
        <span
          key={`${type}-${title || ''}-${String(message)}-${lifetime}`}
          className="alert-progress"
          style={{ animationDuration: `${lifetime}ms` }}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default Alert;
